import type { ParticipantMessageSubListComponent } from "./ParticipantMessageSubListComponent";
import "./ParticipantMessageSubListComponent";
import { type RenderableContentBlock, type ParticipantRole } from "../types.js";
import { sharedSheet } from "../styles/sharedStyles.js";
import componentStylesText from "./MessageHistoryComponent.css?inline";

const componentSheet = new CSSStyleSheet();
componentSheet.replaceSync(componentStylesText);

/**
 * Determine whether the user prefers reduced motion for animated interactions.
 *
 * Smooth turn alignment is the default UX, but accessibility preferences must
 * disable motion when `(prefers-reduced-motion: reduce)` is active.
 */
function prefersReducedMotion(): boolean {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
        return false;
    }

    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/**
 * MessageHistoryComponent — A container component for the entire chat history.
 *
 * It renders a top-level <ul> and groups messages into turn wrappers (.messages__turn).
 * Each turn contains one or more participant sub-lists (user and/or assistant).
 *
 * Turn rules:
 * - A new user action always starts a new turn.
 * - Subsequent assistant output stays in the current turn.
 * - Assistant-only output (e.g. welcome messages) stays in or creates an assistant-only turn
 *   until a user action starts a new turn.
 * - Only the newest turn carries the `messages__turn--latest` CSS class.
 */
export class MessageHistoryComponent extends HTMLElement {
    private list: HTMLUListElement | null = null;
    /** Reference to the current (newest) turn wrapper <li> element. */
    private currentTurn: HTMLLIElement | null = null;

    constructor() {
        super();
    }

    /**
     * Called when the element is first connected to the document's DOM.
     */
    connectedCallback(): void {
        if (!this.shadowRoot) {
            this.render();
        }
    }

    /**
     * Creates the internal <ul> structure.
     */
    private render(): void {
        const shadow = this.attachShadow({ mode: "open" });
        shadow.adoptedStyleSheets = [sharedSheet, componentSheet];

        this.list = document.createElement("ul");
        // Using common class from existing CSS for the main message list
        this.list.className = "messages__list";

        // Copy accessibility attributes from the host element
        const ariaLabel = this.getAttribute("aria-label");
        if (ariaLabel) {
            this.list.setAttribute("aria-label", ariaLabel);
        }
        const ariaLive = this.getAttribute("aria-live");
        if (ariaLive) {
            this.list.setAttribute("aria-live", ariaLive);
        }

        shadow.appendChild(this.list);
    }

    /**
     * Creates a new turn wrapper <li> and appends it to the list.
     * Removes the `messages__turn--latest` class from the previous turn
     * so only the newest turn carries it.
     */
    private createTurn(): HTMLLIElement {
        // Remove latest marker from previous turn
        if (this.currentTurn) {
            this.currentTurn.classList.remove("messages__turn--latest");
        }

        const turn = document.createElement("li");
        turn.className = "messages__turn messages__turn--latest";
        this.list!.appendChild(turn);
        this.currentTurn = turn;
        return turn;
    }

    /**
     * Returns the last participant sub-list inside the current turn, or null.
     */
    private getLastSubListInCurrentTurn(): ParticipantMessageSubListComponent | null {
        if (!this.currentTurn) {
            return null;
        }
        return this.currentTurn.lastElementChild as ParticipantMessageSubListComponent | null;
    }

    /**
     * Creates a new participant sub-list for the given role and appends it
     * to the specified turn wrapper.
     */
    private createSubList(role: ParticipantRole, turn: HTMLLIElement): ParticipantMessageSubListComponent {
        const subList = document.createElement("jt-participant-messages-sub-list") as ParticipantMessageSubListComponent;
        subList.setAttribute("role", role);
        turn.appendChild(subList);
        return subList;
    }

    /**
     * Determines whether a new turn should be created for the given role.
     *
     * A new turn is created when:
     * - No current turn exists yet.
     * - The role is "user" (every user action starts a fresh turn).
     *
     * Assistant content appends to the existing current turn.
     */
    private shouldCreateNewTurn(role: ParticipantRole): boolean {
        if (!this.currentTurn) {
            return true;
        }
        // User messages always start a new turn
        return role === "user";
    }

    /**
     * Adds new message blocks to the history.
     *
     * Turn semantics:
     * - A user message always starts a new turn.
     * - Assistant messages append to the current turn (creating one if none exists).
     * - Within a turn, consecutive blocks of the same role reuse the existing sub-list.
     *
     * @param role The role of the participant ("assistant" or "user").
     * @param data An array of RenderableContentBlock objects.
     */
    addBlocks(role: ParticipantRole, data: RenderableContentBlock[]): void {
        if (!this.list) {
            return;
        }

        // Decide whether we need a new turn
        if (this.shouldCreateNewTurn(role)) {
            this.createTurn();
        }

        const turn = this.currentTurn!;
        let lastSubList = this.getLastSubListInCurrentTurn();

        // Reuse existing sub-list if it matches the role, otherwise create a new one
        if (!lastSubList || lastSubList.getAttribute("role") !== role) {
            lastSubList = this.createSubList(role, turn);
        }

        lastSubList.addBlocks(data);
    }

    /**
     * Sets the viewport height used to reserve empty space below the newest turn.
     *
     * The value is stored as the CSS custom property `--messages-viewport-height`
     * on the host element.  Only `.messages__turn--latest` consumes it as `min-height`.
     *
     * @param height The visible height of the `.messages` scroll container in pixels.
     *               Pass 0 or a negative number to clear the custom property.
     */
    setViewportHeight(height: number): void {
        if (height > 0) {
            this.style.setProperty("--messages-viewport-height", `${height}px`);
        } else {
            this.style.removeProperty("--messages-viewport-height");
        }
    }

    /**
     * Store the current header/footer overlay insets as CSS custom properties.
     *
     * The internal list consumes these values as top/bottom padding so content
     * remains visible below the overlaid header and composer.
     *
     * @param topInset The current header height in pixels.
     * @param bottomInset The current composer height in pixels.
     */
    setViewportInsets(topInset: number, bottomInset: number): void {
        this.style.setProperty("--messages-top-inset", `${Math.max(0, topInset)}px`);
        this.style.setProperty("--messages-bottom-inset", `${Math.max(0, bottomInset)}px`);
    }

    /**
     * Align the latest turn wrapper with the top edge of the provided scroll container.
     *
     * The calculation uses the difference between the latest turn's bounding box
     * and the outer `.messages` container's bounding box so the caller does not
     * need to know about the component's Shadow DOM internals.
     *
     * @param scrollContainer The `.messages` element that owns the visible viewport.
     * @param topInset The visible top inset inside the scroll container, e.g. the
     *                 overlaid header height that must remain unobscured.
     */
    scrollLatestTurnToTop(scrollContainer: HTMLElement, topInset = 0): void {
        if (!this.currentTurn) {
            return;
        }

        const containerRect = scrollContainer.getBoundingClientRect();
        const latestTurnRect = this.currentTurn.getBoundingClientRect();
        const targetScrollTop = Math.max(
            0,
            scrollContainer.scrollTop + latestTurnRect.top - containerRect.top - topInset
        );

        if (typeof scrollContainer.scrollTo === "function") {
            const behavior = prefersReducedMotion() ? "auto" : "smooth";
            scrollContainer.scrollTo({ top: targetScrollTop, behavior });
            return;
        }

        scrollContainer.scrollTop = targetScrollTop;
    }

    /**
     * Sets the loading state for the given role within the current turn.
     *
     * When enabling loading, the assistant sub-list is found or created inside the
     * current turn. When disabling, the sub-list is cleaned up if it is empty.
     *
     * @param role The role to set the loading state for.
     * @param isLoading Whether it should be loading.
     */
    setLoading(role: ParticipantRole, isLoading: boolean): void {
        if (!this.list) {
            return;
        }

        if (isLoading) {
            // Ensure a turn exists for the loading indicator
            if (!this.currentTurn) {
                this.createTurn();
            }

            const turn = this.currentTurn!;
            let lastSubList = this.getLastSubListInCurrentTurn();

            if (!lastSubList || lastSubList.getAttribute("role") !== role) {
                lastSubList = this.createSubList(role, turn);
            }
            lastSubList.isLoading = true;
        } else {
            // Find the last sub-list in the current turn for the given role
            const lastSubList = this.getLastSubListInCurrentTurn();
            if (lastSubList && lastSubList.getAttribute("role") === role) {
                lastSubList.isLoading = false;

                // If it's empty now (no blocks and not loading), remove it to avoid breaking grouping
                if (lastSubList.isEmpty) {
                    lastSubList.remove();
                }
            }
        }
    }
}

if (!customElements.get("jt-message-history")) {
    customElements.define("jt-message-history", MessageHistoryComponent);
}
